import {
    AvailableModalityCoverageStatus,
    AvailablePlanType,
    AvailablePriorAuthorizationIndication,
    AvailableThirdPartyPayerType,
    PayerCoverageCheckNetwork,
    ResetBenefitStatus,
} from "@common-shared/config/coverage.config"
import {
    PolicyModality,
    PolicyModalityCode,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { ISelectRenderedOption } from "@common-shared/types/object.types"

export enum OneVerifyFlagType {
    INACTIVE_POLICY = "INACTIVE_POLICY", // plan_status=inactive
    EXPIRING_SOON = "EXPIRING_SOON", // plan_end_date < 30 days current date
    UNKNOWN_COSTSHARE = "UNKNOWN_COSTSHARE", // 206 + unknown_costshare response error code
    UNKNOWN_ACCUMULATOR = "UNKNOWN_ACCUMULATOR", // 206 + unknown_remaining response error code
    UNKNOWN_MEMBER_OBLIGATION = "UNKNOWN_MEMBER_OBLIGATION", // 206 + other response error code
    POLICY_NOT_FOUND = "POLICY_NOT_FOUND", // 404
    REVIEW_REQUIRED = "REVIEW_REQUIRED", // 400, 500, 502
    DEMOGRAPHICS_MISMATCH = "DEMOGRAPHICS_MISMATCH", // request and response demographics don't match (payer_id, member_id, dob, first / last names)
    THIRD_PARTY_PAYER_DETECTED = "THIRD_PARTY_PAYER_DETECTED", // third_party_payer is not unknown
    ADDITIONAL_POLICY_DETECTED = "ADDITIONAL_POLICY_DETECTED", // additional_policy is not unknown
    UNKNOWN_VISITS = "UNKNOWN_VISITS", // visits_total is null; applies to PT only
    VISITS_MET = "VISITS_MET", // visits_remaining = 0; applies to PT only

    // TODO implement after MVP
    NEW_PATIENT = "NEW_PATIENT", // header that is passed through: EHR integration -> coverage API -> nirvana-backend

    // TODO implement after MVP
    // should compare to previous check, ideally in the future should appear just when happens first time
    // Copayment is present and remaining_stop_loss = 0 -> OOP Max Met
    // Coinsurance is present and remaining_deductible = 0 -> Deductible Met
    // Coinsurance is present and remaining_stop_loss = 0 0> OOP Max Met
    DEDUCTIBLE_MET = "DEDUCTIBLE_MET",
    OOP_MAX_MET = "OOP_MAX_MET",
    CREDENTIALING_ERROR = "CREDENTIALING_ERROR",
    ENROLLMENT_REQUIRED = "ENROLLMENT_REQUIRED",
    MEMBER_ID_MISMATCH = "MEMBER_ID_MISMATCH",
    MEMBER_DOB_MISMATCH = "MEMBER_DOB_MISMATCH",
    MEDICARE_ADVANTAGE = "MEDICARE_ADVANTAGE",
    MANAGED_MEDICAID = "MANAGED_MEDICAID",
    QUALIFIED_MEDICARE_BENEFICIARY = "QUALIFIED_MEDICARE_BENEFICIARY",
    TELEHEALTH_COST_SHARE_DIFFERENCES = "TELEHEALTH_COST_SHARE_DIFFERENCES",
    NETWORK_STATUS_MISMATCH = "NETWORK_STATUS_MISMATCH",
}

export enum CoverageBreakdownSection {
    FLAGS = "FLAGS",
    POLICY_GENERAL = "POLICY_GENERAL",
    POLICY_THIRD_PARTY = "POLICY_THIRD_PARTY",
    POLICY_MEDICARE_ADVANTAGE = "POLICY_MEDICARE_ADVANTAGE",
    POLICY_ADDITIONAL_POLICY = "POLICY_ADDITIONAL_POLICY",
    POLICY_FINANCIALS = "POLICY_FINANCIALS",
    POLICY_OBLIGATION = "POLICY_OBLIGATION",
    SPECIALTY_SPECIFIC = "SPECIALTY_SPECIFIC",
    TELEHEALTH = "TELEHEALTH",
    PRIMARY_CARE_PHYSICIAN = "PRIMARY_CARE_PHYSICIAN",
}

export enum CoveragePatientType {
    PROSPECT = "PROSPECT",
    NEW_PATIENT = "NEW_PATIENT",
    EXISTING_PATIENT = "EXISTING_PATIENT",
}

export enum QuotaUsageType {
    GENERAL = "GENERAL",
    DISCOVER = "DISCOVER",
    SMART_SCAN = "SMART_SCAN",
    MEDICAID = "MEDICAID",
    AGENTIC_PORTAL = "AGENTIC_PORTAL",
    AGENTIC_PHONE = "AGENTIC_PHONE",
}

export enum AgenticCheckStatus {
    NEW = "NEW",
    PENDING = "PENDING",
    FINISHED = "FINISHED",
    FAILED = "FAILED",
}

export const COMMON_ONE_VERIFY_COVERAGE_CONFIG = {
    defaultFallbackCoverageDataValueUnknown: "Unknown",
    defaultFallbackCoverageDataValueNotProvided: "Not provided",
    defaultFallbackCoverageDataValueNone: "None",
    defaultFallbackCoverageDataValueNA: "N/A",

    // Order matters: the first item in each array is the default modality code
    get modalityToModalityCodesMapping(): Record<
        PolicyModality,
        PolicyModalityCode[]
    > {
        return {
            [PolicyModality.MENTAL_HEALTH]: [
                PolicyModalityCode.PSY,
                PolicyModalityCode.PSC,
            ],
            [PolicyModality.PHYSICAL_THERAPY]: [PolicyModalityCode.PHT],
            [PolicyModality.EVALUATION_MANAGEMENT]: [PolicyModalityCode.PMC],
            [PolicyModality.MEDICAL_NUTRITION_THERAPY]: [
                PolicyModalityCode.MNT,
            ],
            [PolicyModality.HOME_SLEEP_THERAPY]: [PolicyModalityCode.HST],
            [PolicyModality.MRI]: [PolicyModalityCode.MRI],
            [PolicyModality.COMPUTED_TOMOGRAPHY]: [PolicyModalityCode.CT],
            [PolicyModality.INTENSIVE_OUTPATIENT_PROGRAM]: [
                PolicyModalityCode.IOP,
            ],
            [PolicyModality.PARTIAL_HOSPITALIZATION_PROGRAM]: [
                PolicyModalityCode.PHP,
            ],
            [PolicyModality.APPLIED_BEHAVIORAL_ANALYSIS]: [
                PolicyModalityCode.ABA,
            ],
            [PolicyModality.OBSTETRICS_GYNECOLOGY]: [PolicyModalityCode.OBG],
            [PolicyModality.MATERNITY]: [PolicyModalityCode.MAT],
            [PolicyModality.ORTHOPEDICS]: [PolicyModalityCode.ORP],
            [PolicyModality.DERMATOLOGY]: [PolicyModalityCode.DER],
            [PolicyModality.GASTROENTEROLOGY]: [PolicyModalityCode.GI],
            [PolicyModality.SPEECH_THERAPY]: [PolicyModalityCode.ST],
            [PolicyModality.CARDIOLOGY]: [PolicyModalityCode.CAR],
            [PolicyModality.OCCUPATIONAL_THERAPY]: [PolicyModalityCode.OT],
            [PolicyModality.LABORATORY]: [PolicyModalityCode.LAB],
            // TODO: remove this comment after coverage api is updated
            // Currently unsupported modalities in coverage api
            [PolicyModality.SUBSTANCE_USE_DISORDER]: [PolicyModalityCode.SUD],
            [PolicyModality.URGENT_CARE]: [PolicyModalityCode.UC],
            [PolicyModality.ENDOCRINOLOGY]: [PolicyModalityCode.END],
            [PolicyModality.NEPHROLOGY]: [PolicyModalityCode.NEP],
            [PolicyModality.NEUROLOGY]: [PolicyModalityCode.NEU],
            [PolicyModality.PULMONOLOGY]: [PolicyModalityCode.PUL],
            [PolicyModality.UROLOGY]: [PolicyModalityCode.URO],
            [PolicyModality.CHRONIC_CARE_MANAGEMENT]: [PolicyModalityCode.CCM],
        }
    },

    get modalityCodeToModalityMapping(): Record<
        PolicyModalityCode,
        PolicyModality
    > {
        return {
            [PolicyModalityCode.PMC]: PolicyModality.EVALUATION_MANAGEMENT,
            [PolicyModalityCode.PHT]: PolicyModality.PHYSICAL_THERAPY,
            [PolicyModalityCode.PSC]: PolicyModality.MENTAL_HEALTH,
            [PolicyModalityCode.PSY]: PolicyModality.MENTAL_HEALTH,
            [PolicyModalityCode.MRI]: PolicyModality.MRI,
            [PolicyModalityCode.HST]: PolicyModality.HOME_SLEEP_THERAPY,
            [PolicyModalityCode.MNT]: PolicyModality.MEDICAL_NUTRITION_THERAPY,
            [PolicyModalityCode.CT]: PolicyModality.COMPUTED_TOMOGRAPHY,
            [PolicyModalityCode.IOP]:
                PolicyModality.INTENSIVE_OUTPATIENT_PROGRAM,
            [PolicyModalityCode.PHP]:
                PolicyModality.PARTIAL_HOSPITALIZATION_PROGRAM,
            [PolicyModalityCode.ABA]:
                PolicyModality.APPLIED_BEHAVIORAL_ANALYSIS,
            [PolicyModalityCode.OBG]: PolicyModality.OBSTETRICS_GYNECOLOGY,
            [PolicyModalityCode.MAT]: PolicyModality.MATERNITY,
            [PolicyModalityCode.ORP]: PolicyModality.ORTHOPEDICS,
            [PolicyModalityCode.DER]: PolicyModality.DERMATOLOGY,
            [PolicyModalityCode.GI]: PolicyModality.GASTROENTEROLOGY,
            [PolicyModalityCode.ST]: PolicyModality.SPEECH_THERAPY,
            [PolicyModalityCode.CAR]: PolicyModality.CARDIOLOGY,
            [PolicyModalityCode.OT]: PolicyModality.OCCUPATIONAL_THERAPY,
            [PolicyModalityCode.LAB]: PolicyModality.LABORATORY,
            // TODO: remove this comment after coverage api is updated
            // Currently unsupported modalities in coverage api
            [PolicyModalityCode.SUD]: PolicyModality.SUBSTANCE_USE_DISORDER,
            [PolicyModalityCode.UC]: PolicyModality.URGENT_CARE,
            [PolicyModalityCode.END]: PolicyModality.ENDOCRINOLOGY,
            [PolicyModalityCode.NEP]: PolicyModality.NEPHROLOGY,
            [PolicyModalityCode.NEU]: PolicyModality.NEUROLOGY,
            [PolicyModalityCode.PUL]: PolicyModality.PULMONOLOGY,
            [PolicyModalityCode.URO]: PolicyModality.UROLOGY,
            [PolicyModalityCode.CCM]: PolicyModality.CHRONIC_CARE_MANAGEMENT,
        }
    },

    get modalityCodeDisplayNameMapping(): Record<PolicyModalityCode, string> {
        return {
            [PolicyModalityCode.PMC]: "Primary Care",
            [PolicyModalityCode.PHT]: "Physical Therapy",
            [PolicyModalityCode.PSC]: "Psychiatry",
            [PolicyModalityCode.PSY]: "Psychotherapy / Mental Health",
            [PolicyModalityCode.MRI]: "MRI",
            [PolicyModalityCode.HST]: "Home Sleep Therapy",
            [PolicyModalityCode.MNT]: "Medical Nutrition Therapy",
            [PolicyModalityCode.CT]: "Computed Tomography",
            [PolicyModalityCode.IOP]: "Intensive Outpatient Program",
            [PolicyModalityCode.PHP]: "Partial Hospitalization Program",
            [PolicyModalityCode.ABA]: "Applied Behavioral Analysis",
            [PolicyModalityCode.OBG]: "Obstetrics and Gynecology",
            [PolicyModalityCode.MAT]: "Maternity",
            [PolicyModalityCode.ORP]: "Orthopedics",
            [PolicyModalityCode.DER]: "Dermatology",
            [PolicyModalityCode.GI]: "Gastroenterology",
            [PolicyModalityCode.ST]: "Speech Therapy",
            [PolicyModalityCode.CAR]: "Cardiology",
            [PolicyModalityCode.OT]: "Occupational Therapy",
            [PolicyModalityCode.LAB]: "Laboratory Testing",
            // TODO: remove this comment after coverage api is updated
            // Currently unsupported modalities in coverage api
            [PolicyModalityCode.SUD]: "Substance Use Disorder",
            [PolicyModalityCode.UC]: "Urgent Care",
            [PolicyModalityCode.END]: "Endocrinology",
            [PolicyModalityCode.NEP]: "Nephrology",
            [PolicyModalityCode.NEU]: "Neurology",
            [PolicyModalityCode.PUL]: "Pulmonology",
            [PolicyModalityCode.URO]: "Urology",
            [PolicyModalityCode.CCM]: "Chronic Care Management",
        }
    },

    get medicaidPlanTypes(): AvailablePlanType[] {
        return [
            AvailablePlanType.ORIGINAL_MEDICAID,
            AvailablePlanType.MANAGED_MEDICAID,
        ]
    },

    get payerCoverageCheckNetworkMapped(): Partial<
        Record<PayerCoverageCheckNetwork, ISelectRenderedOption>
    > {
        return {
            [PayerCoverageCheckNetwork.IN]: {
                value: PayerCoverageCheckNetwork.IN,
                displayValue: "INN",
                dropdownDisplayValue: "In Network (INN)",
            },
            [PayerCoverageCheckNetwork.OUT]: {
                value: PayerCoverageCheckNetwork.OUT,
                displayValue: "OON",
                dropdownDisplayValue: "Out of Network (OON)",
            },
        }
    },

    get availableModalityCoverageStatusMapping(): Record<
        AvailableModalityCoverageStatus,
        string
    > {
        return {
            [AvailableModalityCoverageStatus.ACTIVE]: "Active",
            [AvailableModalityCoverageStatus.INACTIVE]: "Inactive",
            [AvailableModalityCoverageStatus.UNKNOWN]: "Unknown",
        }
    },

    get availableThirdPartyPayerTypeMapping(): Record<
        AvailableThirdPartyPayerType,
        string
    > {
        return {
            [AvailableThirdPartyPayerType.PAYER]: "File Claims To",
            [AvailableThirdPartyPayerType.UTILIZATION_MANAGER]:
                "Provider Network & Coverage",
        }
    },

    get availablePriorAuthorizationMapping(): Record<
        AvailablePriorAuthorizationIndication,
        string
    > {
        return {
            [AvailablePriorAuthorizationIndication.NOT_REQUIRED]:
                "Not Required",
            [AvailablePriorAuthorizationIndication.REQUIRED]: "Required",
            [AvailablePriorAuthorizationIndication.UNKNOWN]: "Unknown",
        }
    },

    get selectRenderedPatientTypes(): Record<
        CoveragePatientType,
        ISelectRenderedOption
    > {
        return {
            [CoveragePatientType.NEW_PATIENT]: {
                value: CoveragePatientType.NEW_PATIENT,
                displayValue: "New Patient",
            },
            [CoveragePatientType.PROSPECT]: {
                value: CoveragePatientType.PROSPECT,
                displayValue: "Prospect",
            },
            [CoveragePatientType.EXISTING_PATIENT]: {
                value: CoveragePatientType.EXISTING_PATIENT,
                displayValue: "Existing Patient",
            },
        }
    },

    get selectRenderedResetBenefitStatus(): Record<
        ResetBenefitStatus,
        ISelectRenderedOption
    > {
        return {
            [ResetBenefitStatus.NOT_DETECTED]: {
                value: ResetBenefitStatus.NOT_DETECTED,
                displayValue: "Not detected",
            },
            [ResetBenefitStatus.DETECTED_ACTION_REQUIRED]: {
                value: ResetBenefitStatus.DETECTED_ACTION_REQUIRED,
                displayValue: "Action required",
            },
            [ResetBenefitStatus.DETECTED_NO_ACTION_REQUIRED]: {
                value: ResetBenefitStatus.DETECTED_NO_ACTION_REQUIRED,
                displayValue: "No action needed",
            },
        }
    },
}
